import { Link } from 'react-router-dom';
import { Mail, Shield } from 'lucide-react';

const FooterLink = ({
  href,
  children,
  external = false,
  disabled = false,
}: {
  href: string;
  children: React.ReactNode;
  external?: boolean;
  disabled?: boolean;
}) => {
  if (disabled) {
    return (
      <span className="text-sm text-white/40 cursor-not-allowed" title="Coming soon">
        {children}
      </span>
    );
  }

  if (external || href.startsWith('mailto:') || href.startsWith('#')) {
    return (
      <a
        href={href}
        className="text-sm text-white/80 hover:text-capledger-teal transition-colors duration-200"
        {...(external ? { target: '_blank', rel: 'noopener noreferrer' } : {})}
        onClick={
          href === '#features'
            ? (e) => {
                e.preventDefault();
                document.getElementById('features')?.scrollIntoView({ behavior: 'smooth' });
              }
            : undefined
        }
      >
        {children}
      </a>
    );
  }

  return (
    <Link
      to={href}
      className="text-sm text-white/80 hover:text-capledger-teal transition-colors duration-200"
    >
      {children}
    </Link>
  );
};

const FooterSection = ({ title, children }: { title: string; children: React.ReactNode }) => {
  return (
    <div className="space-y-4">
      <h3 className="text-sm font-semibold uppercase tracking-wide text-capledger-teal">{title}</h3>
      <nav className="flex flex-col space-y-2" aria-label={`${title} navigation`}>
        {children}
      </nav>
    </div>
  );
};

const Footer = () => {
  return (
    <footer role="contentinfo" className="bg-capledger-navy text-white">
      {/* Main Footer Content */}
      <div className="container mx-auto px-6 py-12">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8 lg:gap-12">
          {/* Product Column */}
          <FooterSection title="Product">
            <FooterLink href="#features">Features</FooterLink>
            <FooterLink href="#" disabled>
              Pricing
            </FooterLink>
            <FooterLink href="/security">Security</FooterLink>
            <FooterLink href="#" disabled>
              Roadmap
            </FooterLink>
            <FooterLink href="/funding/scenarios">Scenario Mode</FooterLink>
            <FooterLink href="https://status.capledger.co.za" external>
              System Status
            </FooterLink>
          </FooterSection>

          {/* Company Column */}
          <FooterSection title="Company">
            <FooterLink href="/about">About Spesvest</FooterLink>
            <a
              href="mailto:hello@capledger.co.za"
              className="flex items-center gap-2 text-sm text-white/80 hover:text-capledger-teal transition-colors duration-200"
            >
              <Mail className="h-4 w-4" />
              Contact Us
            </a>
            <FooterLink href="#" disabled>
              Careers
            </FooterLink>
            <FooterLink href="#" disabled>
              Partners
            </FooterLink>
          </FooterSection>

          {/* Legal Column */}
          <FooterSection title="Legal">
            <FooterLink href="/terms">Terms of Service</FooterLink>
            <FooterLink href="/privacy">Privacy Policy</FooterLink>
            <FooterLink href="/cookies">Cookie Policy</FooterLink>
            <FooterLink href="/data-protection">Data Protection</FooterLink>
            <FooterLink href="/compliance-statement">Compliance</FooterLink>
          </FooterSection>
        </div>
      </div>

      {/* Divider */}
      <div className="border-t border-white/10" />

      {/* Footer Bottom */}
      <div className="container mx-auto px-6 py-6">
        <div className="space-y-4 text-center lg:text-left">
          {/* Copyright & Company Info */}
          <div className="text-sm text-white/80">
            <p className="mb-1">
              © 2025 Spesvest (Pty) Ltd. All rights reserved. CapLedger is a product of Spesvest
              (Pty) Ltd.
            </p>
            <p className="text-xs text-white/60">
              Reg. No. 2016/197925/07 · Unit C, Doornbosch Centre, Strand Rd, Stellenbosch, South
              Africa, 7600
            </p>
          </div>

          {/* Legal Disclaimer */}
          <div className="text-xs text-white/60 max-w-3xl mx-auto lg:mx-0">
            <p>
              CapLedger is a digital equity and company register management platform. We do not
              provide legal, financial, or investment advice.
            </p>
          </div>

          {/* Trust Line */}
          <div className="flex items-center justify-center lg:justify-start gap-2 text-xs text-white/70">
            <Shield className="h-4 w-4 text-capledger-teal" />
            <span>
              Data protected under POPIA and GDPR · Hosted in secure data regions · Encrypted at
              rest and in transit
            </span>
          </div>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
